import { ReactElement, useMemo } from 'react';
import {
  AppBar,
  Chip,
  Link,
  makeStyles,
  Toolbar,
  Typography,
  useTheme,
} from '@material-ui/core';
import HelpOutlined from '@material-ui/icons/HelpOutlined';
import { DateTime } from 'luxon';
import { FontFamily } from '@principal-developer-portal/ui';
import { ZERO_DAY_COLOR } from '../../../feature-flags/zero-day';

export interface AppBarComponentProps {
  title: string;
  subtitle?: string;
  timeStamp: Date | string;
  fullDescription?: string;
  foundDate?: string;
  remediatedAt?: string;
  redoRequired?: boolean;
  redoAt?: string;
  redoReason?: string;
  zeroDay?: boolean;
  removalTimingHref?: string;
}

// eslint-disable-next-line max-lines-per-function
const useStyles = makeStyles(theme => ({
  appbar: {
    width: '1,252px',
    minHeight: '92px',
    padding: '16px 16px',
    gap: '4px',
    borderRadius: '7px',
    background: '#003865',
  },
  zeroDayAppbar: {
    width: '1,252px',
    minHeight: '92px',
    padding: '16px 16px',
    gap: '4px',
    borderRadius: '7px',
    border: `3px solid ${ZERO_DAY_COLOR}`,
    // AppBar defaults to the primary colour, which the dark text would not read against.
    backgroundColor: theme.palette.background.paper,
    color: theme.palette.text.primary,
  },
  titleContainer: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    width: '100%',
    minHeight: '24px',
    gap: '0px',
    marginBottom: '8px',
  },
  title: {
    flex: '1 1 auto',
    whiteSpace: 'pre-line',
    overflow: 'visible',
    overflowWrap: 'break-word',
    wordBreak: 'break-word',
    color: theme.palette.common.white,
    fontFamily: FontFamily.Regular,
    fontWeight: 400,
    fontSize: '16px',
    width: '100%',
  },
  zeroDayTitle: {
    flex: '1 1 auto',
    whiteSpace: 'pre-line',
    overflow: 'visible',
    overflowWrap: 'break-word',
    wordBreak: 'break-word',
    color: theme.palette.text.primary,
    fontFamily: FontFamily.Regular,
    fontWeight: 400,
    fontSize: '16px',
    width: '100%',
  },
  toolbar: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    width: '100%',
    padding: '0px',
    minHeight: 'auto',
  },
  subtitle: {
    flex: '0 1 auto',
    gap: '4px',
    color: theme.palette.common.white,
    minHeight: '24px',
    lineHeight: '24px',
    width: '100%',
    whiteSpace: 'normal',
    overflow: 'visible',
    textOverflow: 'unset',
    overflowWrap: 'break-word',
    wordBreak: 'break-word',
    fontFamily: FontFamily.Bold,
    fontWeight: 700,
    fontSize: '20px',
  },
  zeroDaySubtitle: {
    flex: '0 1 auto',
    gap: '4px',
    color: theme.palette.text.primary,
    minHeight: '24px',
    lineHeight: '24px',
    width: '100%',
    whiteSpace: 'normal',
    overflow: 'visible',
    textOverflow: 'unset',
    overflowWrap: 'break-word',
    wordBreak: 'break-word',
    fontFamily: FontFamily.Bold,
    fontWeight: 700,
    fontSize: '20px',
  },
  subtitleRow: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    width: '100%',
    gap: '8px',
  },
  removalTimingLink: {
    display: 'flex',
    alignItems: 'center',
    flex: '0 0 auto',
    gap: '4px',
    marginTop: '8px',
    color: theme.palette.common.white,
    fontFamily: FontFamily.Bold,
    fontWeight: 700,
    fontSize: '12px',
    whiteSpace: 'nowrap',
    textDecoration: 'none',
    '&:hover': {
      textDecoration: 'none',
    },
  },
  zeroDayRemovalTimingLink: {
    display: 'flex',
    alignItems: 'center',
    flex: '0 0 auto',
    gap: '4px',
    marginTop: '8px',
    color: theme.palette.text.primary,
    fontFamily: FontFamily.Bold,
    fontWeight: 700,
    fontSize: '12px',
    whiteSpace: 'nowrap',
    textDecoration: 'none',
    '&:hover': {
      textDecoration: 'none',
    },
  },
}));

// eslint-disable-next-line max-lines-per-function
export const AppBarComponent = (props: AppBarComponentProps): ReactElement => {
  const theme = useTheme();
  const classes = useStyles(theme);
  const { zeroDay } = props;
  const headerTextColor = zeroDay
    ? theme.palette.text.primary
    : theme.palette.common.white;

  const timestampText = useMemo(() => {
    if (!props.timeStamp) return '';

    try {
      const date = DateTime.fromJSDate(new Date(props.timeStamp));

      return `${date.toLocaleString({
        month: '2-digit',
        day: '2-digit',
        year: 'numeric',
      })} at ${date.toFormat('hh:mm a ZZZZ')}`;
    } catch (error) {
      // TODO: Clean up error handling
      // eslint-disable-next-line no-console
      console.error(error);

      return '';
    }
  }, [props.timeStamp]);

  const dataAsOfText = useMemo(() => {
    if (!props.foundDate) return '';

    const date = DateTime.fromISO(props.foundDate);

    if (!date.isValid) return '';

    return `Data as of: ${date.toLocaleString({
      month: 'numeric',
      day: 'numeric',
      year: 'numeric',
    })}`;
  }, [props.foundDate]);

  return (
    <AppBar
      position="static"
      className={zeroDay ? classes.zeroDayAppbar : classes.appbar}
      data-testid="finding-detail-header"
    >
      <Toolbar className={classes.toolbar}>
        <div className={classes.titleContainer}>
          <div
            style={{ display: 'flex', alignItems: 'center', flex: '1 1 auto' }}
          >
            <Typography
              variant="h2"
              className={zeroDay ? classes.zeroDayTitle : classes.title}
              data-testid="title"
              title={props.title}
              style={{
                color: headerTextColor,
                fontSize: '16px',
                lineHeight: '24px',
                margin: 0,
                padding: 0,
              }}
            >
              {props.title}
            </Typography>
          </div>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'flex-end',
            }}
          >
            <Typography
              variant="body2"
              style={{
                color: headerTextColor,
                fontFamily: FontFamily.Regular,
                fontSize: '14px',
                lineHeight: '24px',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                overflowWrap: 'break-word',
              }}
            >
              {dataAsOfText ||
                (timestampText ? `Last scanned ${timestampText}` : '')}
            </Typography>
          </div>
        </div>
        <div className={classes.subtitleRow}>
          {props.subtitle && (
            <Typography
              variant="h3"
              className={zeroDay ? classes.zeroDaySubtitle : classes.subtitle}
              data-testid="subtitle"
              title={props.subtitle}
              style={{
                fontSize: '20px',
                lineHeight: '24px',
                margin: 0,
                padding: 0,
                whiteSpace: 'normal',
                overflow: 'visible',
                textOverflow: 'unset',
                width: 'auto',
                flex: '1 1 auto',
              }}
            >
              {props.subtitle}
            </Typography>
          )}
          {props.removalTimingHref && (
            <Link
              href={props.removalTimingHref}
              target="_blank"
              rel="noopener noreferrer"
              color="inherit"
              underline="none"
              className={
                zeroDay
                  ? classes.zeroDayRemovalTimingLink
                  : classes.removalTimingLink
              }
              data-testid="removal-timing-link"
            >
              When will this finding be removed from PDP?
              <HelpOutlined style={{ fontSize: '16px', color: 'inherit' }} />
            </Link>
          )}
        </div>{' '}
        {props.remediatedAt && (
          <Chip
            size="small"
            label={
              <Typography
                style={{
                  fontWeight: 700,
                  fontSize: '12px',
                  color: '#0076CF',
                }}
              >
                Remediated {props.remediatedAt}
              </Typography>
            }
            style={{
              marginTop: '8px',
              height: 'auto',
              backgroundColor: '#e3f2fd',
              border: '1px solid #90caf9',
              padding: '2px 0',
            }}
          />
        )}{' '}
        {props.redoRequired && (
          <Chip
            size="small"
            data-testid="remediation-denied-notice"
            label={
              <Typography
                style={{
                  fontWeight: 700,
                  fontSize: '12px',
                  color: '#BF360C',
                  whiteSpace: 'normal',
                }}
              >
                Remediation denied
                {props.redoAt && ` on ${props.redoAt}`}
                {props.redoReason ? `: ${props.redoReason}` : ''}
              </Typography>
            }
            style={{
              marginTop: '8px',
              height: 'auto',
              maxWidth: '100%',
              backgroundColor: '#FFF3E0',
              border: '1px solid #FFB74D',
              padding: '2px 0',
            }}
          />
        )}{' '}
      </Toolbar>
    </AppBar>
  );
};
